import {
  type ExternalToast,
  Toaster as Sonner,
  type ToastClassnames,
  type ToasterProps,
  toast as sonnerToast,
} from "sonner"

type ToastCall =
  | {
      type: "success"
      args: Parameters<(typeof sonnerToast)["success"]>
    }
  | {
      type: "error"
      args: Parameters<(typeof sonnerToast)["error"]>
    }
  | {
      type: "info"
      args: Parameters<(typeof sonnerToast)["info"]>
    }
  | {
      type: "warning"
      args: Parameters<(typeof sonnerToast)["warning"]>
    }
  | {
      type: "custom"
      args: Parameters<(typeof sonnerToast)["custom"]>
    }

let isFullscreenQueueingToasts = false
const toastQueue: ToastCall[] = []

function setFullscreenToastQueueing(isQueueing: boolean) {
  const wasQueueing = isFullscreenQueueingToasts
  isFullscreenQueueingToasts = isQueueing

  if (wasQueueing && !isQueueing) {
    while (toastQueue.length > 0) {
      const call = toastQueue.shift()
      if (!call) continue

      if (call.type === "success") {
        sonnerToast.success(...call.args)
      } else if (call.type === "error") {
        sonnerToast.error(...call.args)
      } else if (call.type === "info") {
        sonnerToast.info(...call.args)
      } else if (call.type === "custom") {
        sonnerToast.custom(...call.args)
      }
    }
  }
}

const appToast = {
  success: (...args: Parameters<(typeof sonnerToast)["success"]>) => {
    if (isFullscreenQueueingToasts) {
      toastQueue.push({ type: "success", args })
      return
    }
    return sonnerToast.success(...args)
  },
  error: (...args: Parameters<(typeof sonnerToast)["error"]>) => {
    if (isFullscreenQueueingToasts) {
      toastQueue.push({ type: "error", args })
      return
    }
    return sonnerToast.error(...args)
  },
  info: (...args: Parameters<(typeof sonnerToast)["info"]>) => {
    if (isFullscreenQueueingToasts) {
      toastQueue.push({ type: "info", args })
      return
    }
    return sonnerToast.info(...args)
  },
  warning: (...args: Parameters<(typeof sonnerToast)["warning"]>) => {
    if (isFullscreenQueueingToasts) {
      toastQueue.push({ type: "warning", args })
      return
    }
    return sonnerToast.warning(...args)
  },
  custom: (...args: Parameters<(typeof sonnerToast)["custom"]>) => {
    if (isFullscreenQueueingToasts) {
      toastQueue.push({ type: "custom", args })
      return
    }
    return sonnerToast.custom(...args)
  },
}

type CustomToastProps = {
  id: string | number
  title: string
  description: string
  button: {
    label: string
    onClick: () => void
  }
}

const Toaster = ({ ...props }: ToasterProps) => {
  return (
    <Sonner
      className="toaster group"
      style={
        {
          "--normal-bg": "var(--surface-background)",
          "--normal-text": "var(--textDefault)",
          "--normal-border": "var(--surface-background)",
        } as React.CSSProperties
      }
      {...props}
    />
  )
}

function successToastClassName(): ToastClassnames {
  return {
    toast: "!bg-feedback-positive-subtle !rounded-4xl !text-feedback-positive-text-subtle !px-4 !py-2 !min-h-12.5",
    title: "!ml-1 !text-feedback-positive-text-subtle !text-base !leading-5",
    description: "!ml-1 !text-feedback-positive-text-subtle !text-sm !leading-4",
  }
}

function errorToastClassName(): ToastClassnames {
  return {
    toast: "!bg-feedback-negative-subtle !rounded-4xl !text-feedback-negative-text-subtle !px-4 !py-2 !min-h-12.5",
    title: "!ml-2 !text-feedback-negative-text-subtle !text-base !leading-5",
    description: "!ml-2 !text-feedback-negative-text-subtle !text-sm !leading-4",
  }
}

function warningToastClassName(): ToastClassnames {
  return {
    toast: "!bg-feedback-notice-subtle !rounded-4xl !text-feedback-notice-text-subtle !px-4 !py-2 !min-h-12.5",
    title: "!ml-2 !text-feedback-notice-text-subtle !text-base !leading-5",
    description: "!ml-2 !text-feedback-notice-text-subtle !text-sm !leading-4",
  }
}

function infoToastClassName(): ToastClassnames {
  return {
    toast:
      "!bg-feedback-information-subtle !rounded-4xl !text-feedback-information-text-subtle !px-4 !py-2 !min-h-12.5",
    title: "!ml-2 !text-feedback-information-text-subtle !text-base !leading-5",
    description: "!ml-2 !text-feedback-information-text-subtle !text-sm !leading-4",
  }
}

/** A fully custom toast that still maintains the animations and interactions. */
function CustomToastLayout(props: CustomToastProps) {
  const { title, description, button, id } = props

  return (
    <div className="flex w-full items-center rounded-lg bg-white p-4 shadow-lg ring-1 ring-black/5 md:max-w-[364px]">
      <div className="flex flex-1 items-center">
        <div className="w-full">
          <p className="text-sm font-medium text-gray-900">{title}</p>
          <p className="mt-1 text-sm text-gray-500">{description}</p>
        </div>
      </div>
      <div className="text-indigo-600 hover:text-indigo-500 focus:ring-indigo-500 ml-5 shrink-0 rounded-md text-sm font-medium focus:ring-2 focus:ring-offset-2 focus:outline-hidden">
        <button
          className="bg-indigo-50 text-indigo-600 hover:bg-indigo-100 rounded px-3 py-1 text-sm font-semibold"
          onClick={() => {
            button.onClick()
            sonnerToast.dismiss(id)
          }}
        >
          {button.label}
        </button>
      </div>
    </div>
  )
}

/** I recommend abstracting the toast function
 *  so that you can call it without having to use toast.custom everytime. */
function customToast(toastProps: Omit<CustomToastProps, "id">, data?: ExternalToast) {
  return sonnerToast.custom(
    (id) => (
      <CustomToastLayout
        id={id}
        title={toastProps.title}
        description={toastProps.description}
        button={toastProps.button}
      />
    ),
    data,
  )
}

export {
  CustomToastLayout,
  Toaster,
  appToast,
  customToast,
  errorToastClassName,
  infoToastClassName,
  setFullscreenToastQueueing,
  sonnerToast,
  successToastClassName,
  warningToastClassName,
}
